import { useContext, useState, type ReactNode } from 'react';
import { assertNever } from '@kofno/piper';
import { BookOpen, ExternalLink, Search } from 'lucide-react';
import { T, translation, TranslationsContext } from '../../../../Translations';
import SectionCard from './SectionCard';

export type AccessSource = 'organization' | 'client_group' | 'private';

export interface CollectionOption {
  id: number;
  name: string;
  experiencesCount: number;
  accessSource: AccessSource;
}

// Small permission chip indicating how the admin can access a collection.
// Organization-wide is the common case; Client Group / Private mark the
// collections surfaced by DVLP-7659.
function AccessSourceChip({ source }: { source: AccessSource }) {
  switch (source) {
    case 'organization':
      // Organization-wide is the default and by far the most common; render no
      // chip to keep the list quiet. Only client-group and private access,
      // which DVLP-7659 newly surfaces, are labelled.
      return null;
    case 'client_group':
      return (
        <PermissionPill>
          <T kind="Client Group" />
        </PermissionPill>
      );
    case 'private':
      return (
        <PermissionPill>
          <T kind="Private" />
        </PermissionPill>
      );
    default:
      return assertNever(source);
  }
}

function PermissionPill({ children }: { children: ReactNode }) {
  return (
    <span className="flex-shrink-0 inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      {children}
    </span>
  );
}

interface Props {
  /** All learning collections available to associate with the group. */
  collections: ReadonlyArray<CollectionOption>;
  /** Currently selected collection IDs. */
  selectedCollectionIds: number[];
  /** Called when the selected set changes. */
  onSelectedCollectionIdsChange: (ids: number[]) => void;
  /** Section number in the parent page's section list. */
  sectionNumber?: number;
  /** Disable all interactions (e.g. during submit). */
  disabled?: boolean;
}

/**
 * Learning collections section for the Add Group / Edit Group workflows.
 *
 * Visual language mirrors Add Participant's `GroupSelectionSection`:
 * search box + selection summary + a scrollable list of individually
 * bordered cards (selected cards get an accent border). Presentational
 * only — all state lives in the parent.
 */
function GroupCollectionsSection({
  collections,
  selectedCollectionIds,
  onSelectedCollectionIdsChange,
  sectionNumber = 3,
  disabled = false,
}: Props) {
  const ts = useContext(TranslationsContext);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleCollection = (id: number) => {
    if (selectedCollectionIds.includes(id)) {
      onSelectedCollectionIdsChange(selectedCollectionIds.filter((cid) => cid !== id));
    } else {
      onSelectedCollectionIdsChange([...selectedCollectionIds, id]);
    }
  };

  const filteredCollections = collections.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const selectedCount = selectedCollectionIds.length;
  const totalCount = collections.length;

  return (
    <SectionCard
      number={sectionNumber}
      title={<T kind="Learning Collections" />}
      description={
        <T kind="Choose which learning collections members see in the Learning platform." />
      }
      intercomTarget="group-collections-section"
    >
      {/* Search */}
      <div className="relative mb-4">
        <input
          type="text"
          placeholder={translation('Search collections...')(ts)}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={disabled}
          className="w-full px-4 py-2 pl-10 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          data-intercom-target="group-collections-search-input"
        />
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      </div>

      {/* Selection Summary */}
      <div className="mb-3 flex items-center justify-between">
        <span className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
          {searchQuery && (
            <>
              <T kind="Showing {{count}} •" count={filteredCollections.length} />{' '}
            </>
          )}
          <T
            kind="{{selectedCount}} of {{totalCount}} selected"
            selectedCount={selectedCount}
            totalCount={totalCount}
          />
        </span>
        {selectedCount > 0 && !disabled && (
          <button
            type="button"
            onClick={() => onSelectedCollectionIdsChange([])}
            className="text-primary hover:opacity-80 transition-opacity"
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
            }}
            data-intercom-target="group-clear-all-collections-button"
          >
            <T kind="Clear all" />
          </button>
        )}
      </div>

      {/* Collection cards */}
      <div className="space-y-3 max-h-80 overflow-y-auto">
        {totalCount === 0 ? (
          <div className="p-8 text-center border border-dashed border-border rounded-lg bg-muted/10">
            <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-40" />
            <h3 className="mb-2" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
              <T kind="No learning collections yet" />
            </h3>
            <p className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
              <T kind="Learning collections give participants a curated set of courses to work through." />
            </p>
          </div>
        ) : filteredCollections.length > 0 ? (
          filteredCollections.map((collection) => {
            const isSelected = selectedCollectionIds.includes(collection.id);
            return (
              <label
                key={collection.id}
                className={`flex items-start gap-3 p-4 border rounded-md cursor-pointer transition-colors ${
                  isSelected
                    ? 'border-accent bg-card'
                    : 'border-border bg-muted/10 hover:border-border'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleCollection(collection.id)}
                  disabled={disabled}
                  className="w-5 h-5 mt-0.5 rounded cursor-pointer text-primary border-border focus:ring-primary"
                  data-intercom-target="group-collection-checkbox"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="truncate"
                      style={{
                        fontSize: 'var(--text-base)',
                        fontWeight: 'var(--font-weight-semibold)',
                      }}
                    >
                      {collection.name}
                    </span>
                    <AccessSourceChip source={collection.accessSource} />
                    <a
                      href={`/learning_collections/${collection.id}/preview`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={translation('Launch participant preview')(ts)}
                      onClick={(e) => e.stopPropagation()}
                      className="text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
                      data-intercom-target="group-collection-preview-link"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      <span>
                        <T kind="{{count}} course" count={collection.experiencesCount} />
                      </span>
                    </div>
                  </div>
                </div>
              </label>
            );
          })
        ) : (
          <div className="p-8 text-center border border-dashed border-border rounded-lg bg-muted/10">
            <Search className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-40" />
            <p className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
              <T kind='No collections found matching "{{query}}"' query={searchQuery} />
            </p>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

export default GroupCollectionsSection;
